import { collection, doc, getDoc, getDocs, limit, orderBy, query } from '@firebase/firestore';

const CURRENCY_SETTINGS_COLLECTION = 'Settings';
const CURRENCY_SETTINGS_DOC = 'currency';
const FEE_PAYMENT_SETTINGS_DOC = 'feePaymentDetails';
const EXCHANGE_RATE_HISTORY_COLLECTION = 'ExchangeRateHistory';

const normalizeText = (value) => String(value || '').trim();

export const normalizeBankAccounts = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item, index) => ({
      id: String(item?.id || `item-${index}-${Math.random().toString(36).slice(2, 8)}`),
      bankName: normalizeText(item?.bankName || item?.bank || item?.name),
      accountName: normalizeText(item?.accountName || item?.accountHolder || item?.account_holder),
      accountNumber: normalizeText(item?.accountNumber || item?.account_no || item?.number),
      note: normalizeText(item?.note || item?.description)
    }))
    .filter((item) => item.bankName || item.accountName || item.accountNumber || item.note);
};

export const normalizePaybills = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item, index) => ({
      id: String(item?.id || `item-${index}-${Math.random().toString(36).slice(2, 8)}`),
      paybillNumber: normalizeText(item?.paybillNumber || item?.paybill || item?.number),
      accountNumber: normalizeText(item?.accountNumber || item?.account || item?.reference),
      note: normalizeText(item?.note || item?.description)
    }))
    .filter((item) => item.paybillNumber || item.accountNumber || item.note);
};

export const normalizeTills = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item, index) => ({
      id: String(item?.id || `item-${index}-${Math.random().toString(36).slice(2, 8)}`),
      tillNumber: normalizeText(item?.tillNumber || item?.till || item?.number),
      note: normalizeText(item?.note || item?.description)
    }))
    .filter((item) => item.tillNumber || item.note);
};

export const formatDateTime = (value, fallback = 'Not updated yet') => {
  if (!value) return fallback;

  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return fallback;

  return dateObj.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatNumber = (value, maximumFractionDigits = 4) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';

  return number.toLocaleString('en-KE', {
    maximumFractionDigits
  });
};

export const formatRate = (value, maximumFractionDigits = 4) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';

  return number.toLocaleString('en-KE', {
    minimumFractionDigits: 0,
    maximumFractionDigits
  });
};

export const loadBusinessInfoSnapshot = async (db) => {
  const [currencySnap, paymentDetailsSnap, historySnap] = await Promise.all([
    getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, CURRENCY_SETTINGS_DOC)),
    getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, FEE_PAYMENT_SETTINGS_DOC)),
    getDocs(query(
      collection(db, EXCHANGE_RATE_HISTORY_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(8)
    ))
  ]);

  const currencySettings = currencySnap.exists() ? currencySnap.data() : null;
  const paymentDetails = paymentDetailsSnap.exists() ? paymentDetailsSnap.data() : null;

  const exchangeRateHistory = [];
  historySnap.forEach((historyDoc) => {
    exchangeRateHistory.push({ id: historyDoc.id, ...historyDoc.data() });
  });

  return {
    currencySettings,
    paymentDetails,
    exchangeRateHistory,
    bankAccounts: normalizeBankAccounts(paymentDetails?.bankAccounts || paymentDetails?.banks || []),
    paybills: normalizePaybills(paymentDetails?.paybills || paymentDetails?.paybillNumbers || []),
    tills: normalizeTills(paymentDetails?.tills || paymentDetails?.tillNumbers || [])
  };
};
